/* assets/lifestyle-bundle-cart.js
   ✅ Stable Dawn cart drawer sync for Lifestyle add-ons.
   - NO fetch monkeypatching
   - After syncing, forces Dawn drawer section re-render (no refresh)
*/

(() => {
  // ====== CONFIG ======
  const ADDON_SMALL_VARIANT_ID = 50988833145117; // +$20 each beyond first small per hoodie
  const ADDON_LARGE_VARIANT_ID = 50988832784669; // +$100 each

  // Identify “bundle main product” lines by presence of these properties
  const MAIN_PROP_KEYS = [
    "Customizer: Small logos total",
    "Customizer: Large logos total",
    "Customizer: Small front positions",
    "Customizer: Small back positions",
    "Customizer: Large front",
    "Customizer: Large back"
  ];

  // Dawn drawer sections we want back
  const DRAWER_SECTIONS = ["cart-drawer", "cart-icon-bubble", "cart-live-region-text"];

  // ====== INTERNAL STATE ======
  let syncing = false;
  let scheduled = null;

  function hasAnyMainProps(properties) {
    if (!properties) return false;
    return MAIN_PROP_KEYS.some((k) => Object.prototype.hasOwnProperty.call(properties, k));
  }

  function toInt(val, fallback = 0) {
    const n = parseInt(String(val ?? ""), 10);
    return Number.isFinite(n) ? n : fallback;
  }

  function getCartDrawerEl() {
    return document.querySelector("cart-drawer");
  }

  async function fetchCart() {
    const res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
    if (!res.ok) throw new Error("Failed to fetch /cart.js");
    return await res.json();
  }

  async function cartChangeByKey(key, quantity) {
    const res = await fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        id: key,
        quantity,
        sections: DRAWER_SECTIONS,
        sections_url: window.location.pathname
      })
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data?.description || "Failed to POST /cart/change.js");
    return data;
  }

  async function cartAddVariant(variantId, quantity) {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        items: [{ id: variantId, quantity }],
        sections: DRAWER_SECTIONS,
        sections_url: window.location.pathname
      })
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data?.description || "Failed to POST /cart/add.js");
    return data;
  }

  function renderDrawerFromResponseJson(data) {
    const drawer = getCartDrawerEl();
    if (drawer && typeof drawer.renderContents === "function" && data && data.sections) {
      drawer.renderContents(data);
      return true;
    }
    return false;
  }

  function computeDesiredAddonTotals(cart) {
    let desiredSmall = 0;
    let desiredLarge = 0;

    for (const item of cart.items || []) {
      if (!hasAnyMainProps(item.properties)) continue;

      const qty = toInt(item.quantity, 1);
      const smallTotal = toInt(item.properties?.["Customizer: Small logos total"], 0);
      const largeTotal = toInt(item.properties?.["Customizer: Large logos total"], 0);

      const smallAddonPerUnit = Math.max(0, smallTotal - 1);
      const largeAddonPerUnit = Math.max(0, largeTotal);

      desiredSmall += smallAddonPerUnit * qty;
      desiredLarge += largeAddonPerUnit * qty;
    }

    return { desiredSmall, desiredLarge };
  }

  function findAddonLines(cart) {
    let smallLine = null;
    let largeLine = null;

    for (const item of cart.items || []) {
      if (Number(item.id) === Number(ADDON_SMALL_VARIANT_ID)) smallLine = item;
      if (Number(item.id) === Number(ADDON_LARGE_VARIANT_ID)) largeLine = item;
    }
    return { smallLine, largeLine };
  }

  async function syncAddonsToCart() {
    if (syncing) return;
    syncing = true;

    try {
      const cart = await fetchCart();
      const { desiredSmall, desiredLarge } = computeDesiredAddonTotals(cart);
      const { smallLine, largeLine } = findAddonLines(cart);

      const currentSmall = smallLine ? toInt(smallLine.quantity, 0) : 0;
      const currentLarge = largeLine ? toInt(largeLine.quantity, 0) : 0;

      // Nothing to do
      if (desiredSmall === currentSmall && desiredLarge === currentLarge) return;

      let lastResponseWithSections = null;

      // Small
      if (desiredSmall !== currentSmall) {
        if (smallLine) {
          lastResponseWithSections = await cartChangeByKey(smallLine.key, desiredSmall);
        } else if (desiredSmall > 0) {
          lastResponseWithSections = await cartAddVariant(ADDON_SMALL_VARIANT_ID, desiredSmall);
        }
      }

      // Large
      if (desiredLarge !== currentLarge) {
        if (largeLine) {
          lastResponseWithSections = await cartChangeByKey(largeLine.key, desiredLarge);
        } else if (desiredLarge > 0) {
          lastResponseWithSections = await cartAddVariant(ADDON_LARGE_VARIANT_ID, desiredLarge);
        }
      }

      // ✅ Force drawer to show the updated cart (no refresh)
      if (lastResponseWithSections) {
        renderDrawerFromResponseJson(lastResponseWithSections);
      }
    } finally {
      syncing = false;
    }
  }

  function scheduleSync(delay = 250) {
    if (scheduled) clearTimeout(scheduled);
    scheduled = setTimeout(() => {
      scheduled = null;
      syncAddonsToCart().catch(console.error);
    }, delay);
  }

  // ====== LISTENERS ======

  function bindDrawerListeners() {
    const drawer = getCartDrawerEl();
    if (!drawer) return;

    // Any qty / remove change inside drawer should trigger a resync after Dawn updates
    drawer.addEventListener(
      "change",
      (e) => {
        const t = e.target;
        if (!(t instanceof HTMLInputElement)) return;
        const name = (t.getAttribute("name") || "").toLowerCase();
        const id = (t.getAttribute("id") || "").toLowerCase();
        if (name.includes("updates") || name.includes("quantity") || id.includes("quantity")) {
          scheduleSync(400);
        }
      },
      true
    );

    drawer.addEventListener(
      "click",
      (e) => {
        const el = e.target instanceof Element ? e.target : null;
        if (!el) return;
        const btn = el.closest("button, a");
        if (!btn) return;

        // qty +/- or remove buttons
        const text =
          (btn.getAttribute("name") || "") +
          " " +
          (btn.getAttribute("aria-label") || "") +
          " " +
          (btn.textContent || "");

        if (/plus|minus|remove|delete|update|quantity|qty/i.test(text)) {
          scheduleSync(500);
        }
      },
      true
    );
  }

  // Your customizer dispatches this after adding
  document.addEventListener("lifestyle:cart-changed", () => scheduleSync(250));

  function init() {
    // Only relevant if you have a drawer/cart system present
    bindDrawerListeners();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();